<script setup lang="ts">
import { computed } from "vue"
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline"
import type { ConfirmationType } from "@/composables/useConfirmation"
import UiModal from "./UiModal.vue"

const props = withDefaults(
  defineProps<{
    title?: string
    message: string
    confirmText?: string
    cancelText?: string
    type?: ConfirmationType
    showIcon?: boolean
    focusConfirm?: boolean
    zIndex?: 10 | 20 | 30 | 40 | 50 | 60
  }>(),
  {
    type: "info",
    confirmText: "Confirmar",
    cancelText: "Cancelar",
    showIcon: true,
    focusConfirm: false,
    zIndex: 60,
  }
)

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const modelValue = defineModel<boolean>({ required: true })

const iconComponent = computed(() => {
  const icons = {
    info: InformationCircleIcon,
    success: CheckCircleIcon,
    warning: ExclamationTriangleIcon,
    danger: XCircleIcon,
  }
  return icons[props.type!]
})

const iconColorClass = computed(() => {
  const colors = { info: "text-blue-600", success: "text-green-600", warning: "text-yellow-600", danger: "text-red-600" }
  return colors[props.type!]
})

const iconBgClass = computed(() => {
  const bg = { info: "bg-blue-100", success: "bg-green-100", warning: "bg-yellow-100", danger: "bg-red-100" }
  return bg[props.type!]
})

const confirmButtonClass = computed(() => {
  const classes = {
    info: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
    success: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
    warning: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
    danger: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
  }
  return classes[props.type!]
})

const handleConfirm = () => {
  emit("confirm")
  modelValue.value = false
}

const handleCancel = () => {
  emit("cancel")
  modelValue.value = false
}
</script>

<template>
  <UiModal
    v-model="modelValue"
    size="sm"
    :close-on-click-outside="false"
    :close-on-escape="true"
    :show-close-button="false"
    :z-index="zIndex"
  >
    <div class="flex items-start gap-5 py-4">
      <div v-if="showIcon" :class="['flex-shrink-0 rounded-full p-3', iconBgClass]">
        <component :is="iconComponent" :class="['w-6 h-6', iconColorClass]" />
      </div>

      <div class="flex-1 pt-1 max-h-[60vh] overflow-y-auto pr-1">
        <h3 v-if="title" class="text-lg font-semibold text-slate-900 mb-2">
          {{ title }}
        </h3>
        <slot name="content">
          <p class="text-sm text-slate-600 leading-relaxed whitespace-pre-line break-words">
            {{ message }}
          </p>
        </slot>
      </div>
    </div>

    <template #footer>
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
        @click="handleCancel"
      >
        {{ cancelText }}
      </button>
      <button
        type="button"
        :class="[
          'px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors',
          confirmButtonClass,
        ]"
        :autofocus="focusConfirm"
        @click="handleConfirm"
      >
        {{ confirmText }}
      </button>
    </template>
  </UiModal>
</template>
